import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, ContentSwitcher, InlineNotification, Modal, Search, Select, SelectItem, Switch, Tag, TextArea, TextInput,
} from '@carbon/react';
import { formatVnd, GAME_GROUP_LABEL, GAME_HEALTH_LABEL, type CatalogGame, type GameHealth } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

type Visibility = 'shown' | 'hidden' | 'maintenance';
// live state of the game (API lib/game-health.ts)
type Health = { status: GameHealth; he: 'A' | 'B' | null; lastBet: string | null; bets24h: number; players24h: number; stuck: number; stop: string | null };
type Game = CatalogGame & { state: Visibility; reason: string | null; note?: string; health: Health };
type ListMeta = { version: number; source: 'cms' | 'default'; at: string | null };
type Data = { games: Game[]; meta: { an: ListMeta; baoTri: ListMeta }; pending: { id: string; status: string }[] };
type Draft = { state: Visibility; reason: string };

const STATES: Visibility[] = ['shown', 'hidden', 'maintenance'];
const STATE_LABEL: Record<Visibility, string> = { shown: 'Hiện', hidden: 'Ẩn', maintenance: 'Bảo trì' };
const HEALTH_TAG: Record<GameHealth, 'green' | 'gray' | 'warm-gray' | 'magenta' | 'red'> = {
  ok: 'green', idle: 'gray', none: 'warm-gray', stuck: 'magenta', stopped: 'magenta', down: 'red',
};
// a game players would open onto an error or an empty screen
const BROKEN: GameHealth[] = ['none', 'down'];

function HealthCell({ h }: { h: Health }) {
  return (
    <>
      <Tag size="sm" type={HEALTH_TAG[h.status]}>{GAME_HEALTH_LABEL[h.status]}</Tag>
      <div className="muted vis__note">
        {h.status === 'none' ? 'Không có máy chủ ghi sổ cho game này'
          : h.stop ? h.stop
            : <>{h.bets24h ? <><span className="num">{formatVnd(h.bets24h)}</span> lượt cược, <span className="num">{formatVnd(h.players24h)}</span> người chơi trong 24 giờ</>
              : 'Chưa có cược trong 24 giờ'}{h.lastBet ? <>. Cược gần nhất <When v={h.lastBet} /></> : null}</>}
        {h.stuck ? <div><span className="num">{h.stuck}</span> vé đang chạy quá 30 phút</div> : null}
      </div>
    </>
  );
}

function ListSource({ label, m }: { label: string; m: ListMeta }) {
  return (
    <span>
      {label}: {m.source === 'default'
        ? <span className="muted">danh sách mặc định (chưa sửa từ CMS)</span>
        : <>phiên bản <span className="num">{m.version}</span>{m.at ? <span className="muted">, <When v={m.at} /></span> : null}</>}
    </span>
  );
}

function SaveDialog({ open, changes, games, meta, onClose, onDone }: {
  open: boolean; changes: [string, Draft][]; games: Map<string, Game>; meta: Data['meta'];
  onClose: () => void; onDone: (cmds: Command[]) => void;
}) {
  const m = useMutation({
    mutationFn: () => api.put<{ commands: Command[] }>('/api/admin/games/visibility', {
      changes: changes.map(([id, d]) => ({ id, state: d.state })),
      expect: { an: meta.an.version, baoTri: meta.baoTri.version },
    }),
    onSuccess: (r) => onDone(r.commands),
  });
  const brokenShown = changes.filter(([id, d]) => d.state !== 'hidden' && BROKEN.includes(games.get(id)?.health.status ?? 'ok'));
  const ready = !m.isPending;
  return (
    <Modal open={open} modalHeading={`Lưu ${changes.length} thay đổi`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
      primaryButtonDisabled={!ready} onRequestClose={onClose} onRequestSubmit={() => ready && m.mutate()}>
      <div className="stack">
        <table className="plain">
          <thead><tr><th>Game</th><th>Trước</th><th>Sau</th></tr></thead>
          <tbody>
            {changes.map(([id, d]) => {
              const g = games.get(id);
              return (
                <tr key={id}>
                  <td className="is-title">{g?.name ?? id} <span className="muted num">{id}</span></td>
                  <td data-label="Trước">{g ? STATE_LABEL[g.state] : '-'}</td>
                  <td data-label="Sau">{STATE_LABEL[d.state]}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {brokenShown.length ? (
          <InlineNotification kind="warning" lowContrast hideCloseButton title="Game chưa chạy sẽ lộ ra sảnh"
            subtitle={`${brokenShown.map(([id]) => `${games.get(id)?.name ?? id} (${GAME_HEALTH_LABEL[games.get(id)!.health.status].toLowerCase()})`).join(', ')}. Người chơi bấm vào sẽ gặp lỗi hoặc màn hình trống.`} />
        ) : null}
        <p className="muted">Thay đổi áp dụng khi người chơi tải lại trang. Người đang ở trong game không bị đẩy ra.</p>
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

export function GameVisibilityPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['games'], queryFn: () => api.get<Data>('/api/admin/games') });
  const [draft, setDraft] = useState<Map<string, Draft>>(new Map());
  const [text, setText] = useState('');
  const [group, setGroup] = useState('');
  const [state, setState] = useState('');
  const [health, setHealth] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<Command[] | null>(null);

  const games = useMemo(() => new Map((q.data?.games ?? []).map((g) => [g.id, g])), [q.data]);
  const eff = (g: Game): Draft => draft.get(g.id) ?? { state: g.state, reason: g.reason ?? '' };
  const edit = (g: Game, d: Draft) => setDraft((old) => {
    const n = new Map(old);
    if (d.state === g.state && (d.state === 'shown' || d.reason === (g.reason ?? ''))) n.delete(g.id);
    else n.set(g.id, d);
    return n;
  });

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { meta, pending } = q.data;
  const all = q.data.games;
  const needle = text.trim().toLowerCase();
  const rows = all.filter((g) => (!group || g.group === group) && (!health || g.health.status === health) && (!state || eff(g).state === state)
    && (!needle || g.name.toLowerCase().includes(needle) || g.id.toLowerCase().includes(needle)));
  const count = (s: Visibility) => all.filter((g) => eff(g).state === s).length;
  const editable = can('games.visibility');

  return (
    <div className="page page--with-bar">
      <PageHeader title="Ẩn hiện game"
        lead="Ẩn: game biến khỏi mọi danh sách ở sảnh (tab, bong bóng mini game, banner, hũ). Bảo trì: vẫn thấy ô game nhưng bấm vào báo đang bảo trì. Cột kỹ thuật lấy từ dữ liệu thật: lượt cược tiền thật trong sổ giao dịch 24 giờ qua, vé treo, lệnh tạm dừng và máy chủ có trả lời không (cập nhật mỗi phút)." />
      <div className="row health">
        <ListSource label="Danh sách ẩn" m={meta.an} />
        <ListSource label="Danh sách bảo trì" m={meta.baoTri} />
        {pending.length ? <span className="muted">{pending.length} lệnh đang gửi</span> : null}
      </div>
      {result ? (
        <InlineNotification kind={result.every((c) => c.status === 'SUCCEEDED') ? 'success' : 'warning'} lowContrast onClose={() => setResult(null)}
          title={result.every((c) => c.status === 'SUCCEEDED') ? 'Hệ A đã nhận danh sách mới' : 'Có lệnh chưa xong'}
          subtitle={result.map((c) => `${String(c.payload.ten) === 'an_game' ? 'Danh sách ẩn' : 'Danh sách bảo trì'}: ${c.status === 'SUCCEEDED' ? 'xong' : (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status}`).join('. ')} />
      ) : null}
      <div className="toolbar-filters">
        <Search size="md" labelText="Tìm game" placeholder="Tên hoặc mã game" value={text} onChange={(e) => setText(e.target.value)} className="toolbar-filters__grow" />
        <Select id="gv-group" labelText="Nhóm" value={group} onChange={(e) => setGroup(e.target.value)}>
          <SelectItem value="" text="Tất cả nhóm" />
          {Object.entries(GAME_GROUP_LABEL).map(([k, v]) => <SelectItem key={k} value={k} text={v} />)}
        </Select>
        <Select id="gv-state" labelText="Trạng thái" value={state} onChange={(e) => setState(e.target.value)}>
          <SelectItem value="" text={`Tất cả (${all.length})`} />
          {STATES.map((s) => <SelectItem key={s} value={s} text={`${STATE_LABEL[s]} (${count(s)})`} />)}
        </Select>
        <Select id="gv-health" labelText="Kỹ thuật" value={health} onChange={(e) => setHealth(e.target.value)}>
          <SelectItem value="" text="Tất cả" />
          {Object.entries(GAME_HEALTH_LABEL).map(([k, v]) => <SelectItem key={k} value={k} text={`${v} (${all.filter((g) => g.health.status === k).length})`} />)}
        </Select>
      </div>
      {rows.length ? (
        <table className="plain vis">
          <thead><tr><th>Game</th><th>Nhóm</th><th>Kỹ thuật</th><th>Trạng thái</th></tr></thead>
          <tbody>
            {rows.map((g) => {
              const d = eff(g);
              const changed = draft.has(g.id);
              return (
                <tr key={g.id} className={changed ? 'is-changed' : undefined}>
                  <td className="is-title">
                    <div className="vis__name">{g.name}</div>
                    <div className="muted num">{g.id}</div>
                  </td>
                  <td data-label="Nhóm">{GAME_GROUP_LABEL[g.group]}</td>
                  <td data-label="Kỹ thuật">
                    <HealthCell h={g.health} />
                    {g.note ? <div className="muted vis__note">{g.note}</div> : null}
                  </td>
                  <td data-label="Trạng thái">
                    <ContentSwitcher size="sm" selectedIndex={STATES.indexOf(d.state)} className="vis__switch"
                      onChange={(e) => editable && edit(g, { ...d, state: STATES[e.index ?? 0] ?? 'shown' })}>
                      {STATES.map((s) => <Switch key={s} name={s} text={STATE_LABEL[s]} disabled={!editable} />)}
                    </ContentSwitcher>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <EmptyState title="Không có game khớp bộ lọc" />}
      {draft.size ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{draft.size}</span> game đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={() => setDraft(new Map())}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" onClick={() => setSaving(true)}>Lưu</Button>
        </div>
      ) : null}
      <SaveDialog open={saving} changes={[...draft]} games={games} meta={meta} onClose={() => setSaving(false)}
        onDone={(cmds) => { setSaving(false); setDraft(new Map()); setResult(cmds); qc.invalidateQueries({ queryKey: ['games'] }); }} />
    </div>
  );
}
